import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { PresentationalChatRef } from '@cotera/client/v0/chat/presentational-chat';
import type { CocoChatModel } from '../models/coco-chat.model';
import type { PendingChatSubmission } from '../models/pending-chat-submission';
import { peekChatDraft, removeChatDraft } from '../models/chat-draft';

/** ~5s at 60fps — safety cap so the mount poll can't spin forever. */
const MAX_MOUNT_POLL_FRAMES = 300;

/**
 * Restore an unsent chat draft — persisted to sessionStorage before a reload
 * (e.g. the service-worker auto-reload on a new deploy) — back into the composer
 * once it's on screen.
 *
 * The composer mounts only after `isLoading` clears (a skeleton is shown) and
 * then a short AnimatePresence exit animation, so `chatRef` is still null the
 * instant loading flips. This hook therefore:
 *   - gates on `!isLoading` (in deps) so it restarts when the fetch completes —
 *     otherwise a slow message fetch would burn the rAF cap before the input
 *     exists and the draft would never apply; and
 *   - polls for the input via requestAnimationFrame to cover the brief mount +
 *     animation window, backing off if the user starts typing first.
 *
 * A pending navigation seed always takes priority, and restore runs at most once
 * per conversation. We also refuse to restore while a send is in flight: clearing
 * the agent/workflow URL seed after submit flips `pending` to null and would
 * otherwise re-apply a just-sent draft into the composer.
 */
export const useRestoreChatDraft = (params: {
  cocoModel: CocoChatModel;
  conversationId: string;
  pending: PendingChatSubmission | null;
  isLoading: boolean;
  chatRef: RefObject<PresentationalChatRef>;
}): void => {
  const { cocoModel, conversationId, pending, isLoading, chatRef } = params;
  const restoredForRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      pending !== null ||
      isLoading ||
      restoredForRef.current === conversationId
    ) {
      return;
    }
    const status = cocoModel.statusWatchable.snapshot();
    if (status === 'submitted' || status === 'streaming') {
      // A send is in flight (or just finished clearing the URL seed). Drop any
      // leftover draft and don't try again when status returns to ready —
      // otherwise the just-sent prompt can reappear in the composer.
      removeChatDraft(conversationId);
      restoredForRef.current = conversationId;
      return;
    }
    const draft = peekChatDraft(conversationId);
    if (draft === null) {
      return;
    }
    let raf = 0;
    let attempts = 0;
    const tryRestore = () => {
      // User started typing before we could restore — leave their input alone.
      if (cocoModel.inputWatchable.snapshot().trim() !== '') {
        restoredForRef.current = conversationId;
        return;
      }
      // A send may have started while we were waiting for the composer to mount.
      const liveStatus = cocoModel.statusWatchable.snapshot();
      if (liveStatus === 'submitted' || liveStatus === 'streaming') {
        restoredForRef.current = conversationId;
        removeChatDraft(conversationId);
        return;
      }
      if (chatRef.current === null) {
        if (attempts > MAX_MOUNT_POLL_FRAMES) {
          return;
        }
        attempts += 1;
        raf = requestAnimationFrame(tryRestore);
        return;
      }
      restoredForRef.current = conversationId;
      // Consume so a later pending→null transition (URL seed cleared after send)
      // cannot re-apply the same draft.
      removeChatDraft(conversationId);
      if (draft.editorState !== undefined) {
        chatRef.current.setInputEditorState(draft.editorState);
        return;
      }
      chatRef.current.setInputValue(draft.text ?? '');
    };
    raf = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(raf);
  }, [cocoModel, conversationId, pending, isLoading, chatRef]);
};
